const defaultTheme = require('tailwindcss/defaultTheme');

module.exports = {
    // Bug with tailwind where hover states were getting stuck - https://github.com/tailwindlabs/tailwindcss/pull/8394
    future: {
        hoverOnlyWhenSupported: true,
    },
    content: [
        './src/pages/**/*.{js,ts,jsx,tsx}',
        './src/components/**/*.{js,ts,jsx,tsx}',
        './src/icons/**/*.{js,ts,jsx,tsx}',
        './node_modules/react-tailwindcss-datepicker/dist/index.esm.js',
    ],
    theme: {
        screens: {
            ...defaultTheme.screens,
            // See as discussion for setting maxwidth breakpoints https://github.com/tailwindlabs/tailwindcss/discussions/7645
            '-lg': { max: '1023px' },
            '3xl': '108rem',
        },
        extend: {
            maxWidth: {
                xs: '57.813rem', //925px
                sm: '68.75rem', //1100px,
                md: '86.25rem', //1380px
                lg: '103.75rem', //1660px
                xl: '120rem', //1920px
                footer: '98.438rem', //1575px
            },
            minWidth: {
                card: 'clamp(30vw, 50vh, 50vh)',
            },
            minHeight: {
                hero: 'max(50svh, 400px)',
                heroFullScreen: 'max(100svh, 600px)',
                heroFullScreen90vh: 'max(90svh, 600px)',
            },
            fontSize: {
                sm: '0.875rem', // sm:p-small
                base: '1rem', // lg:h6 & lg:p-small | md:h6 & md:p-small | sm:h5 & sm:p-mid & sm-bq
                md: '1.125rem', // lg:li | md:li | sm:p-large
                lg: '1.25rem', // sm:h4
                xl: '1.3125rem', // lg:p-mid | md:p-mid
                '2xl': '1.5rem', // lg:h5 & lg:p-large | md:h5 & md:p-large | sm:h3
                '3xl': '2rem', // md:H4 | sm:H2
                '4xl': '2.25rem', // lg:H4 & lg:BQ | md:H3 & md:BQ
                '5xl': '2.5rem', // sm:H1
                '6xl': '3rem', // lg:H3 | md:H2
                '7xl': '3.5rem', // md:H2
                '8xl': '4.5rem', // lg:H2 | md:H1
                '9xl': '5.625rem', //@ lg:H1,
            },
            lineHeight: {
                114: '114%',
                120: '120%',
                130: '130%',
                137.5: '137.5%',
                140: '140%',
                142: '142%',
                159: '159%',
            },
            colors: {
                primary: '#112745',
                'sky-blue': '#223A5C',
                'sea-blue': '#768D98',
                sand: '#FBF6EF',
                rock: '#BAC7D0',
                shell: { DEFAULT: '#E9ECF0', 25: '#F9FAFB', 50: '#F3F5F7' },
                white: '#FFFFFF',
                'light-gray': '#F5F5F5',
                black: '#000000',
                grey: '#D9D9D9',
                destructive: '#DC3545',
            },
            keyframes: {
                'accordion-down': {
                    from: { height: 0 },
                    to: { height: 'var(--radix-accordion-content-height)' },
                },
                'accordion-up': {
                    from: { height: 'var(--radix-accordion-content-height)' },
                    to: { height: 0 },
                },
                'arrow-in': {
                    '0%, 100%': {
                        transform: 'translate3d(-20px, 10px, 0px)',
                        opacity: 0,
                    },
                    '99%': { transform: 'translate3d(0px, 0px, 0px)', opacity: 1 },
                },
                'nav-in': {
                    from: { transform: 'translate3d(-100%,0,0)' },
                    to: { transform: 'translate3d(0,0,0)' },
                },
                'nav-out': {
                    from: { transform: 'translate3d(0,0,0)' },
                    to: { transform: 'translate3d(-100%,0,0)' },
                },
                overlayShow: {
                    from: { opacity: 0 },
                    to: { opacity: 1 },
                },
                contentShow: {
                    from: { opacity: 0, transform: 'translate(-50%, -48%) scale(0.96)' },
                    to: { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
                },
                'dot-bounce': {
                    '0%, 100%': {
                        transform: 'translateY(-75%)',
                        'animation-timing-function': 'cubic-bezier(0.8, 0, 1, 1)',
                    },
                    '50%': {
                        transform: 'translateY(0)',
                        'animation-timing-function': 'cubic-bezier(0, 0, 0.2, 1)',
                    },
                },
                'fade-in': {
                    from: { opacity: 0 },
                    to: { opacity: 1, visibility: 'visible' },
                },
                'fade-out': {
                    from: { opacity: 1 },
                    to: { opacity: 0, visibility: 'hidden' },
                },
            },
            animation: {
                'accordion-down': 'accordion-down 0.2s ease-out',
                'accordion-up': 'accordion-up 0.2s ease-out',
                'arrow-in': 'arrow-in 1s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                'nav-in': 'nav-in 300ms cubic-bezier(0.22, 1, 0.36, 1)',
                'nav-out': 'nav-out 300ms cubic-bezier(0.22, 1, 0.36, 1)',
                overlayShow: 'overlayShow 400ms cubic-bezier(0.16, 1, 0.3, 1)',
                contentShow: 'contentShow 1s cubic-bezier(0.16, 1, 0.3, 1)',
                'dot-1': 'dot-bounce 1s infinite',
                'dot-2': 'dot-bounce 1s 0.25s infinite',
                'dot-3': 'dot-bounce 1s 0.5s infinite',
                'dot-4': 'dot-bounce 1s 0.75s infinite',
                fadein: 'fade-in 1s ease-in-out 0.25s forwards',
                fadeout: 'fade-out 1s ease-out 0.25s forwards',
            },
            typography: ({ theme }) => ({
                DEFAULT: {
                    css: {
                        '--tw-prose-body': theme('colors.primary'),
                        '--tw-prose-headings': theme('colors.primary'),
                        '--tw-prose-lead': theme('colors.primary'),
                        '--tw-prose-links': theme('colors.primary'),
                        '--tw-prose-bold': theme('colors.primary'),
                        '--tw-prose-counters': theme('colors.primary'),
                        '--tw-prose-bullets': theme('colors.primary'),
                        '--tw-prose-quotes': theme('colors.primary'),
                        '--tw-prose-quote-borders': theme('colors.primary'),
                        '--tw-prose-captions': theme('colors.primary'),
                    },
                },
            }),
        },
    },
    plugins: [require('@tailwindcss/typography')],
};
